import { useCallback, useEffect, useState } from "react";
import { adminApi, type AdminPermissionRow } from "api/adminApi";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  Input,
  Modal,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  TSkeleton,
  Table,
  Toggle,
} from "pages/admin/ui";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

type Draft = {
  isNew: boolean;
  email: string;
  isAllMenus: boolean;
  menus: string[];
};

// 권한 관리: 어드민 콘솔에 들어올 수 있는 계정과 열 수 있는 메뉴를 정한다. 전 메뉴 권한자만 연다.
// 내 계정은 바꾸거나 지울 수 없다 (서버도 거부) — 권한 관리를 할 사람이 사라지지 않게.
export default function PermissionModal({
  myEmail,
  menus,
  onClose,
}: {
  myEmail: string;
  // 좌측 nav 메뉴 (key = 서버 메뉴 키)
  menus: { key: string; label: string }[];
  onClose: () => void;
}) {
  const [rows, setRows] = useState<AdminPermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.permissionList();
      if (res.code === 0) {
        setRows(res.result ?? []);
        setMessage(null);
      } else {
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const menuLabel = (row: AdminPermissionRow) =>
    menus
      .filter(({ key }) => row.menus.includes(key))
      .map(({ label }) => label)
      .join(" · ") || "-";

  const toggleMenu = (key: string, checked: boolean) =>
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            menus: checked
              ? [...prev.menus, key]
              : prev.menus.filter((m) => m !== key),
          }
        : prev
    );

  const save = async () => {
    if (!draft || saving) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await adminApi.permissionSave({
        email: draft.email.trim(),
        isAllMenus: draft.isAllMenus,
        menus: draft.isAllMenus ? [] : draft.menus,
      });
      if (res.code === 0) {
        setDraft(null);
        await load();
      } else {
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (email: string) => {
    if (deletingEmail !== null) return;
    if (
      !window.confirm(
        `${email} 의 어드민 권한을 지울까요?\n지우면 어드민 콘솔에 들어올 수 없어요.`
      )
    ) {
      return;
    }
    setDeletingEmail(email);
    setMessage(null);
    try {
      const res = await adminApi.permissionDelete(email);
      if (res.code === 0) await load();
      else setMessage(res.message || FALLBACK_ERROR_MESSAGE);
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setDeletingEmail(null);
    }
  };

  const canSave =
    !!draft &&
    draft.email.trim() !== "" &&
    (draft.isAllMenus || draft.menus.length > 0);

  return (
    <Modal
      title="권한 관리"
      width="720px"
      description="어드민 콘솔에 들어올 수 있는 @orwellhealth.org 구글 계정과 열 수 있는 메뉴를 정해요. 전체 권한이면 나중에 생기는 메뉴와 이 권한 관리까지 열려요. 바꾼 권한은 바로 적용되고, 좌측 메뉴는 그 계정이 새로고침하면 바뀌어요."
      onClose={onClose}
    >
      <div className="mt-4 flex justify-end">
        <Button
          variant="primary"
          disabled={!!draft}
          onClick={() =>
            setDraft({ isNew: true, email: "", isAllMenus: false, menus: [] })
          }
        >
          계정 추가
        </Button>
      </div>

      {draft && (
        <div className="mt-3 flex flex-col gap-3 rounded-md border border-slate-200 bg-slate-50 p-4">
          <Field label="이메일">
            <Input
              value={draft.email}
              placeholder="name@orwellhealth.org"
              disabled={!draft.isNew}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </Field>
          <Toggle
            label="전체 권한"
            helper="모든 메뉴와 권한 관리를 열어요"
            checked={draft.isAllMenus}
            onChange={(next) => setDraft({ ...draft, isAllMenus: next })}
          />
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {menus.map(({ key, label }) => (
              <label
                key={key}
                className={`flex items-center gap-2 text-sm ${
                  draft.isAllMenus ? "text-slate-400" : "text-slate-700"
                }`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#0F55A7]"
                  disabled={draft.isAllMenus}
                  checked={draft.isAllMenus || draft.menus.includes(key)}
                  onChange={(e) => toggleMenu(key, e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button disabled={saving} onClick={() => setDraft(null)}>
              취소
            </Button>
            <Button
              variant="primary"
              disabled={!canSave}
              loading={saving}
              loadingLabel="저장 중…"
              onClick={save}
            >
              저장
            </Button>
          </div>
        </div>
      )}

      {message && (
        <div className="mt-3">
          <ErrorBanner>{message}</ErrorBanner>
        </div>
      )}

      <div className="mt-3">
        <Table>
          <THead>
            <TH>이메일</TH>
            <TH>메뉴</TH>
            <TH />
          </THead>
          <tbody>
            {loading && <TSkeleton colSpan={3} />}
            {!loading && rows.length === 0 && (
              <TEmpty colSpan={3}>
                {message ? "목록을 불러오지 못했어요" : "등록된 계정이 없어요"}
              </TEmpty>
            )}
            {rows.map((row) => {
              const isMe = row.email === myEmail;
              return (
                <TRow key={row.email}>
                  <TD className="font-medium">
                    {row.email}
                    {isMe && (
                      <span className="ml-2">
                        <Badge tone="info">나</Badge>
                      </span>
                    )}
                  </TD>
                  <TD>
                    {row.isAllMenus ? (
                      <Badge tone="ok">전체 권한</Badge>
                    ) : (
                      menuLabel(row)
                    )}
                  </TD>
                  <TD>
                    {/* 내 계정은 바꾸거나 지울 수 없다 */}
                    {!isMe && (
                      <span className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!!draft}
                          onClick={() =>
                            setDraft({
                              isNew: false,
                              email: row.email,
                              isAllMenus: row.isAllMenus,
                              menus: row.isAllMenus ? [] : row.menus,
                            })
                          }
                        >
                          수정
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={
                            !!draft ||
                            (deletingEmail !== null &&
                              deletingEmail !== row.email)
                          }
                          loading={deletingEmail === row.email}
                          loadingLabel="삭제 중…"
                          onClick={() => remove(row.email)}
                        >
                          삭제
                        </Button>
                      </span>
                    )}
                  </TD>
                </TRow>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Modal>
  );
}
